import { useEffect, useRef, useState } from "preact/hooks";

import { Snackbar } from "../components/Snackbar";
import { useContacts } from "../hooks/useContacts";
import { useFocusHeadingOnMount } from "../hooks/useFocusHeadingOnMount";
import { useIsDesktop } from "../hooks/useIsDesktop";
import { CONTACTS_PAGE_SUBHEADING } from "../lib/recordsCopy";
import { ContactsView } from "./ContactsView";

export interface ContactsScreenProps {
  /** `app.tsx` guards the `/contacts` ROUTE itself (an unauthorized deep
   * link redirects to `/more` before this component ever mounts), so this
   * is always `true` in practice — still threaded through explicitly so
   * `useContacts`'s own `enabled` gate stays the single source of truth
   * for whether `GET /contacts` ever fires, matching `ProjectsScreen`'s
   * exact precedent. */
  canHandOutWork: boolean;
  /** Sprint-013 C3 (design-spec-animal-cards-2026-07-10.md §5, SA ruling
   * sa-ruling-crud-endpoints-2026-07-10.md Decision 3) — admin-only gate
   * for the Add/Edit/Archive/Restore affordances `ContactsView` renders,
   * threaded from `app.tsx`'s `adminGate`, mirroring
   * `ProjectsScreen`/`AnimalsScreen`'s identical `isAdmin` prop exactly. */
  isAdmin: boolean;
  onBack: () => void;
}

/**
 * Sprint-010 C4 (design-phase-c.md §4, ADR-002 Amendment 6 decision 8) —
 * the routed `/contacts` screen: a small roster (vets/farriers/vendors),
 * name-ascending, `.admin-list`/`.admin-row` — never `DataTable` (no sort/
 * filter/CSV need for a small list, same reasoning Phase B used for the
 * Activity feed). Same routed-screen shape as `ProjectsScreen.tsx`/
 * `AnimalsScreen.tsx` (desktop bare `<h1>`, phone `.more-subheader`
 * "‹ Back to More").
 *
 * Sprint-013 C3 — narrowed to a thin routing/chrome wrapper (heading,
 * back-button, `Snackbar`) around the new `ContactsView`, which now owns
 * the list/toolbar/toggle/detail-overlay/CRUD logic — the exact same split
 * `ProjectsScreen.tsx`/`ProjectsView.tsx` already established in C2. Gains
 * its own local `Snackbar` (same 5s auto-dismiss convention as
 * `ProjectsScreen.tsx`/`AnimalsScreen.tsx`), needed now that
 * `ContactDetailOverlay` has real write affordances to confirm.
 */
export function ContactsScreen({ canHandOutWork, isAdmin, onBack }: ContactsScreenProps) {
  const contacts = useContacts(canHandOutWork);
  const isDesktop = useIsDesktop();
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  useFocusHeadingOnMount(headingRef);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!snackbarMessage) return;
    const timer = setTimeout(() => setSnackbarMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [snackbarMessage]);

  return (
    <section class="view" aria-label="Contacts">
      {isDesktop ? (
        <header class="page-header">
          <h1 ref={headingRef} tabIndex={-1} class="view-heading">
            Contacts
          </h1>
          <p class="view-subheading">{CONTACTS_PAGE_SUBHEADING}</p>
        </header>
      ) : (
        <>
          <div class="more-subheader">
            <button type="button" class="icon-btn" aria-label="Back to More" onClick={onBack}>
              ‹
            </button>
            <h1 ref={headingRef} tabIndex={-1} class="view-heading">
              Contacts
            </h1>
          </div>
          <p class="view-subheading">{CONTACTS_PAGE_SUBHEADING}</p>
        </>
      )}

      <ContactsView contacts={contacts} isAdmin={isAdmin} onNotify={setSnackbarMessage} />
      <Snackbar
        state={snackbarMessage ? { message: snackbarMessage } : null}
        onDismiss={() => setSnackbarMessage(null)}
      />
    </section>
  );
}
